import React, { useCallback, useEffect, useState, useRef, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { removeLocalItem, removeSessionItem, setSessionItem } from "@/utils/safeStorage";
import { AuthContext, type AppRole, type SignupAccountMode, type SignupOAuthIntentOptions } from "@/hooks/auth-context";
import { GOOGLE_CALENDAR_SCOPE_REQUESTED_KEY } from "@/lib/authFlowStorageKeys";
import { beginSignupAnalyticsIntent, type SignupAnalyticsProvider } from "@/lib/signupAnalytics";

const GOOGLE_CALENDAR_EVENTS_READONLY_SCOPE = "https://www.googleapis.com/auth/calendar.events.readonly";

const GOOGLE_AUTH_SCOPES = [
  "openid",
  "email",
  "profile",
  GOOGLE_CALENDAR_EVENTS_READONLY_SCOPE,
].join(" ");

const BASIC_GOOGLE_AUTH_SCOPES = ["openid", "email", "profile"].join(" ");

const ROLE_BOOTSTRAP_RETRY_DELAYS_MS = [150, 300];

function persistOAuthSignupIntent(provider: SignupAnalyticsProvider, signupIntent?: SignupOAuthIntentOptions | null) {
  if (!signupIntent?.role) return;

  setSessionItem("signupRole", signupIntent.role);
  if (signupIntent.accountMode) {
    setSessionItem("signupAccountMode", signupIntent.accountMode);
  } else {
    removeSessionItem("signupAccountMode");
  }

  beginSignupAnalyticsIntent({
    role: signupIntent.role,
    authProvider: provider,
    trialAccountMode: signupIntent.accountMode,
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const rolesRef = useRef<AppRole[]>([]);
  const rolesUserIdRef = useRef<string | null>(null);
  const profileEnsuredRef = useRef<Set<string>>(new Set());
  const rolesRequestIdRef = useRef(0);
  const sessionBootstrapRequestIdRef = useRef(0);
  const establishedUserIdRef = useRef<string | null>(null);

  const applyRoles = useCallback((nextRoles: AppRole[], userId: string | null) => {
    rolesRef.current = nextRoles;
    rolesUserIdRef.current = userId;
    setRoles(nextRoles);
  }, []);

  /**
   * Ensures the current user has a profile row.
   * Calls the ensure-profile edge function which uses service role to create if missing.
   * Idempotent - safe to call multiple times.
   */
  const ensureProfile = async (): Promise<boolean> => {
    if (!session?.user?.id) {
      console.warn("[ensureProfile] No session available");
      return false;
    }

    // Skip if already ensured this session
    if (profileEnsuredRef.current.has(session.user.id)) {
      return true;
    }

    try {
      const { error } = await supabase.functions.invoke("ensure-profile");

      if (error) {
        console.error("[ensureProfile] Edge function error:", error);
        return false;
      }

      profileEnsuredRef.current.add(session.user.id);
      console.log("[ensureProfile] Profile ensured for:", session.user.id);
      return true;
    } catch (err) {
      console.error("[ensureProfile] Error:", err);
      return false;
    }
  };

  const fetchUserRoles = useCallback(async (userId: string): Promise<AppRole[]> => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching roles:", error);
        return [];
      }

      return data?.map((r) => r.role as AppRole) ?? [];
    } catch (err) {
      console.error("Error fetching roles:", err);
      return [];
    }
  }, []);

  const refreshRoles = useCallback(async (targetUserId?: string) => {
    const resolvedUserId = targetUserId ?? establishedUserIdRef.current;
    if (!resolvedUserId) {
      applyRoles([], null);
      return;
    }

    const requestId = ++rolesRequestIdRef.current;
    const existingRoles = rolesRef.current;
    const isSameUser = rolesUserIdRef.current === resolvedUserId;
    const shouldRetryEmptyBootstrap = existingRoles.length === 0;
    let fetchedRoles: AppRole[] = [];

    for (let attempt = 0; attempt <= ROLE_BOOTSTRAP_RETRY_DELAYS_MS.length; attempt += 1) {
      fetchedRoles = await fetchUserRoles(resolvedUserId);
      if (requestId !== rolesRequestIdRef.current) {
        return;
      }

      if (fetchedRoles.length > 0 || !shouldRetryEmptyBootstrap || attempt === ROLE_BOOTSTRAP_RETRY_DELAYS_MS.length) {
        break;
      }

      await new Promise((resolve) => setTimeout(resolve, ROLE_BOOTSTRAP_RETRY_DELAYS_MS[attempt]));
      if (requestId !== rolesRequestIdRef.current) {
        return;
      }
    }

    if (fetchedRoles.length === 0 && existingRoles.length > 0 && isSameUser) {
      console.warn("[AuthProvider] Ignoring transient empty role refresh for active user");
      return;
    }

    applyRoles(fetchedRoles, resolvedUserId);
  }, [applyRoles, fetchUserRoles]);

  useEffect(() => {
    let disposed = false;
    let authEventGeneration = 0;
    const scheduledBootstraps = new Set<ReturnType<typeof setTimeout>>();

    const prepareSessionState = (nextSession: Session | null) => {
      const requestId = ++sessionBootstrapRequestIdRef.current;
      // Invalidate pending reads immediately, before the deferred auth callback runs.
      rolesRequestIdRef.current += 1;
      if (!nextSession?.user || establishedUserIdRef.current !== nextSession.user.id) {
        establishedUserIdRef.current = null;
        setLoading(true);
        setSession(null);
        setUser(null);
        applyRoles([], null);
      } else {
        // Publish fresh credentials immediately; role reads remain deferred.
        setSession(nextSession);
        setUser(nextSession.user);
      }
      return requestId;
    };

    const applySessionState = async (nextSession: Session | null, requestId: number) => {
      if (disposed || requestId !== sessionBootstrapRequestIdRef.current) return;

      if (!nextSession?.user) {
        rolesRequestIdRef.current += 1;
        if (requestId !== sessionBootstrapRequestIdRef.current) {
          return;
        }
        setSession(null);
        setUser(null);
        applyRoles([], null);
        profileEnsuredRef.current.clear();
        setLoading(false);
        return;
      }

      await refreshRoles(nextSession.user.id);
      if (requestId !== sessionBootstrapRequestIdRef.current) {
        return;
      }
      setSession(nextSession);
      setUser(nextSession.user);
      establishedUserIdRef.current = nextSession.user.id;
      setLoading(false);
    };

    const scheduleSessionState = (nextSession: Session | null) => {
      const requestId = prepareSessionState(nextSession);
      const timeoutId = setTimeout(() => {
        scheduledBootstraps.delete(timeoutId);
        void applySessionState(nextSession, requestId);
      }, 0);
      scheduledBootstraps.add(timeoutId);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (disposed) return;
        authEventGeneration += 1;
        scheduleSessionState(newSession);
      }
    );

    const initialAuthEventGeneration = authEventGeneration;
    const initialBootstrapRequestId = sessionBootstrapRequestIdRef.current;
    void supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (disposed || authEventGeneration !== initialAuthEventGeneration ||
          sessionBootstrapRequestIdRef.current !== initialBootstrapRequestId) return;
      void applySessionState(existingSession, prepareSessionState(existingSession));
    });

    return () => {
      disposed = true;
      sessionBootstrapRequestIdRef.current += 1;
      rolesRequestIdRef.current += 1;
      scheduledBootstraps.forEach((timeoutId) => clearTimeout(timeoutId));
      scheduledBootstraps.clear();
      subscription.unsubscribe();
    };
  }, [applyRoles, refreshRoles]);

  const signUp = async (
    email: string,
    password: string,
    fullName: string | undefined,
    intendedRole: AppRole,
    options?: { accountMode?: SignupAccountMode | null },
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    const accountMode = intendedRole === "leader" ? options?.accountMode ?? null : null;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          role: intendedRole, // used server-side to create user_roles deterministically
          ...(accountMode ? { account_mode: accountMode } : {}),
        },
      },
    });

    // If signup succeeded and we have a session, ensure profile immediately
    if (!error && data.session) {
      try {
        await supabase.functions.invoke("ensure-profile");
        profileEnsuredRef.current.add(data.user!.id);
      } catch (err) {
        console.warn("[signUp] Could not ensure profile immediately:", err);
      }
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signInWithGoogle = async (
    nextPath?: string,
    options?: { forceConsent?: boolean; includeCalendarScope?: boolean; signupIntent?: SignupOAuthIntentOptions | null },
  ) => {
    const safeNextPath =
      typeof nextPath === "string" && nextPath.startsWith("/") && !nextPath.startsWith("//")
        ? nextPath
        : null;
    const redirectUrl = safeNextPath
      ? `${window.location.origin}/post-auth?next=${encodeURIComponent(safeNextPath)}`
      : `${window.location.origin}/post-auth`;
    const shouldRequestCalendarScope = options?.includeCalendarScope === true;
    const queryParams: Record<string, string> = {};

    if (shouldRequestCalendarScope) {
      queryParams.access_type = "offline";
      setSessionItem(GOOGLE_CALENDAR_SCOPE_REQUESTED_KEY, "true");
    } else {
      removeSessionItem(GOOGLE_CALENDAR_SCOPE_REQUESTED_KEY);
    }

    if (options?.forceConsent) {
      queryParams.prompt = "consent";
    }

    persistOAuthSignupIntent("google", options?.signupIntent);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectUrl,
        scopes:
          shouldRequestCalendarScope
            ? GOOGLE_AUTH_SCOPES
            : BASIC_GOOGLE_AUTH_SCOPES,
        ...(Object.keys(queryParams).length > 0 ? { queryParams } : {}),
      },
    });

    return { error: error as Error | null };
  };

  const signInWithLinkedIn = async (nextPath?: string) => {
    const safeNextPath =
      typeof nextPath === "string" && nextPath.startsWith("/") && !nextPath.startsWith("//")
        ? nextPath
        : null;
    const redirectUrl = safeNextPath
      ? `${window.location.origin}/post-auth?next=${encodeURIComponent(safeNextPath)}`
      : `${window.location.origin}/post-auth`;

    // If the user is already signed in, we should link LinkedIn to the current account
    // instead of starting a fresh OAuth sign-in flow that can switch to another user.
    const {
      data: { session: currentSession },
    } = await supabase.auth.getSession();

    if (currentSession) {
      type LinkIdentityAuth = {
        linkIdentity?: (params: {
          provider: "linkedin_oidc";
          options?: {
            redirectTo?: string;
            scopes?: string;
          };
        }) => Promise<{ error: Error | null }>;
      };

      const authWithLink = supabase.auth as typeof supabase.auth & LinkIdentityAuth;
      if (typeof authWithLink.linkIdentity === "function") {
        const { error } = await authWithLink.linkIdentity({
          provider: "linkedin_oidc",
          options: {
            redirectTo: redirectUrl,
            scopes: "openid profile email",
          },
        });
        return { error: error as Error | null };
      }

      return {
        error: new Error("LinkedIn account linking is unavailable in this client version."),
      };
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "linkedin_oidc",
      options: {
        redirectTo: redirectUrl,
        scopes: "openid profile email",
        skipBrowserRedirect: false,
      },
    });
    return { error: error as Error | null };
  };

  const signInWithAzure = async (nextPath?: string, options?: { signupIntent?: SignupOAuthIntentOptions | null }) => {
    const safeNextPath =
      typeof nextPath === "string" && nextPath.startsWith("/") && !nextPath.startsWith("//")
        ? nextPath
        : null;
    const redirectUrl = safeNextPath
      ? `${window.location.origin}/post-auth?next=${encodeURIComponent(safeNextPath)}`
      : `${window.location.origin}/post-auth`;

    persistOAuthSignupIntent("microsoft", options?.signupIntent);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        redirectTo: redirectUrl,
        scopes: "openid profile email offline_access",
        skipBrowserRedirect: false,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Goal: clear local session immediately, stop any further authed calls,
    // then best-effort revoke server session.

    // 1) Clear in-memory auth state first (prevents app from thinking we're authed)
    sessionBootstrapRequestIdRef.current += 1;
    rolesRequestIdRef.current += 1;
    establishedUserIdRef.current = null;
    applyRoles([], null);
    profileEnsuredRef.current.clear();
    setSession(null);
    setUser(null);
    setLoading(false);

    // 2) Clear any auth-related storage flags that can trigger /post-auth flows
    try {
      removeSessionItem("signupRole");
      removeSessionItem("signupAccountMode");
      removeSessionItem(GOOGLE_CALENDAR_SCOPE_REQUESTED_KEY);
      removeSessionItem("pendingInvite");
      removeSessionItem("pendingInviteToken");
      removeLocalItem("pendingInvite");
      removeLocalItem("pendingInviteToken");
    } catch {
      // ignore
    }

    // 3) Clear local Supabase session (this is the critical part for “signed back in” loops)
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // ignore
    }

    // Extra safety: clear persisted auth token (covers rare cases where signOut(local) is skipped/fails)
    try {
      // Supabase persists the session in localStorage under sb-<project-ref>-auth-token.
      // We remove any matching keys to guarantee the session cannot be restored.
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }

    // 4) Best-effort revoke server session (may fail if token already expired/rotated)
    try {
      await supabase.auth.signOut({ scope: "global" });
    } catch {
      // ignore
    }

    // 5) Hard redirect to auth and replace history
    window.location.replace("/auth");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        roles,
        signUp,
        signIn,
        signInWithGoogle,
        signInWithLinkedIn,
        signInWithAzure,
        signOut,
        ensureProfile,
        refreshRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
